import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Avatar,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  Breadcrumbs,
  Link,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  AttachFile,
  MoreVert,
  Schedule,
  Archive,
  Flag,
  Person,
  Email,
  School,
  PriorityHigh,
  Check,
  AccessTime,
  Reply,
} from '@mui/icons-material';

interface Message {
  id: number;
  senderId: number;
  senderName: string;
  senderType: 'parent' | 'teacher';
  content: string;
  timestamp: string;
  attachments?: string[];
  isRead: boolean;
}

interface ThreadInfo {
  id: number;
  parentName: string;
  parentEmail: string;
  childName: string;
  childGrade: string;
  subject: string;
  priority: 'normal' | 'high' | 'urgent';
  status: 'active' | 'resolved' | 'pending';
  createdAt: string;
}

const TeacherMessageThread: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [threadInfo, setThreadInfo] = useState<ThreadInfo | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [markAsResolvedDialog, setMarkAsResolvedDialog] = useState(false);

  useEffect(() => {
    if (!threadId) return;

    // Mock data - replace with API call
    setLoading(true);
    setTimeout(() => {
      const mockThreadInfo: ThreadInfo = {
        id: parseInt(threadId),
        parentName: 'Jennifer Johnson',
        parentEmail: 'jennifer.johnson@email.com',
        childName: 'Emma Johnson',
        childGrade: '6th Grade',
        subject: 'Mathematics - Homework Concerns',
        priority: 'high',
        status: 'active',
        createdAt: '2024-01-10T10:30:00Z'
      };

      const mockMessages: Message[] = [
        {
          id: 1,
          senderId: 101,
          senderName: 'Jennifer Johnson',
          senderType: 'parent',
          content: 'Hi Ms. Wilson, I wanted to discuss Emma\'s recent struggles with algebra homework. She seems to be having difficulty understanding the concepts we\'re covering this week.',
          timestamp: '2024-01-10T10:30:00Z',
          isRead: true
        },
        {
          id: 2,
          senderId: 201,
          senderName: 'Ms. Sarah Wilson',
          senderType: 'teacher',
          content: 'Thank you for reaching out, Jennifer. I\'ve noticed Emma has been quiet during our algebra lessons lately. I\'d be happy to provide some additional practice problems and perhaps schedule a brief meeting to discuss strategies.',
          timestamp: '2024-01-10T14:15:00Z',
          isRead: true
        },
        {
          id: 3,
          senderId: 101,
          senderName: 'Jennifer Johnson',
          senderType: 'parent',
          content: 'That would be wonderful! Emma mentioned that she understands the basic concepts but gets confused when the problems become more complex. Would it be possible to get some step-by-step examples?',
          timestamp: '2024-01-10T16:45:00Z',
          isRead: true
        },
        {
          id: 4,
          senderId: 101,
          senderName: 'Jennifer Johnson',
          senderType: 'parent',
          content: 'Also, could we schedule a meeting to discuss this further? I think it would be beneficial to have a face-to-face conversation about Emma\'s progress.',
          timestamp: '2024-01-10T16:47:00Z',
          isRead: false
        }
      ];

      setThreadInfo(mockThreadInfo);
      setMessages(mockMessages);
      setLoading(false);
    }, 1000);
  }, [threadId]);

  useEffect(() => {
    // Scroll to bottom when new messages are added
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    
    // Mock sending - replace with API call
    setTimeout(() => {
      const newMsg: Message = {
        id: messages.length + 1,
        senderId: 201, // Teacher ID
        senderName: 'Ms. Sarah Wilson',
        senderType: 'teacher',
        content: newMessage,
        timestamp: new Date().toISOString(),
        isRead: false
      };

      setMessages(prev => [...prev, newMsg]);
      setNewMessage('');
      setSending(false);
    }, 1000);
  };

  const handleScheduleMeeting = () => {
    setMeetingDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleMarkAsResolved = () => {
    setMarkAsResolvedDialog(true);
    setMenuAnchor(null);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 1 week
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  if (loading || !threadInfo) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/teacher-dashboard')}
            sx={{ cursor: 'pointer' }}
          >
            Teacher Dashboard
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/teacher/messages')}
            sx={{ cursor: 'pointer' }}
          >
            Messages
          </Link>
          <Typography color="text.primary">Conversation</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigate('/teacher/messages')}>
                <ArrowBack />
              </IconButton>
              <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                {threadInfo.parentName.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {threadInfo.parentName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Email sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  {threadInfo.parentEmail}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Person sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  Parent of {threadInfo.childName} ({threadInfo.childGrade})
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label={threadInfo.priority} 
                color={getPriorityColor(threadInfo.priority) as any}
                size="small" 
              />
              <Chip 
                label={threadInfo.status} 
                color={threadInfo.status === 'resolved' ? 'success' : 'primary'}
                size="small" 
              />
              <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <MoreVert />
              </IconButton>
            </Box>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {threadInfo.subject}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Started {new Date(threadInfo.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Messages */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ maxHeight: '60vh', overflowY: 'auto', p: 2 }}>
            {messages.map((message, index) => (
              <Box key={message.id} sx={{ mb: 3 }}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: message.senderType === 'teacher' ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-start',
                    gap: 2
                  }}
                >
                  {message.senderType === 'parent' && (
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {message.senderName.charAt(0)}
                    </Avatar>
                  )}
                  
                  <Paper 
                    sx={{ 
                      p: 2, 
                      maxWidth: '70%',
                      bgcolor: message.senderType === 'teacher' ? 'primary.main' : 'grey.100',
                      color: message.senderType === 'teacher' ? 'white' : 'text.primary'
                    }}
                  >
                    <Typography variant="body1" paragraph sx={{ mb: 1 }}>
                      {message.content}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: message.senderType === 'teacher' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <AccessTime sx={{ fontSize: 12 }} />
                        {formatTimestamp(message.timestamp)}
                      </Typography>
                      
                      {message.senderType === 'teacher' && (
                        <Check 
                          sx={{ 
                            fontSize: 16, 
                            color: message.isRead ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.4)' 
                          }} 
                        />
                      )}
                    </Box>
                  </Paper>
                  
                  {message.senderType === 'teacher' && (
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      {message.senderName.charAt(0)}
                    </Avatar>
                  )}
                </Box>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>
        </CardContent>
      </Card>

      {/* Message Input */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type your reply..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <IconButton color="primary">
                <AttachFile />
              </IconButton>
              <Button
                variant="contained"
                endIcon={sending ? <CircularProgress size={16} /> : <Send />}
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending}
              >
                {sending ? 'Sending...' : 'Send'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={handleScheduleMeeting}>
          <ListItemIcon>
            <Schedule />
          </ListItemIcon>
          <ListItemText>Schedule Meeting</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMarkAsResolved}>
          <ListItemIcon>
            <Check />
          </ListItemIcon>
          <ListItemText>Mark as Resolved</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <ListItemIcon>
            <Archive />
          </ListItemIcon>
          <ListItemText>Archive Conversation</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <ListItemIcon>
            <Flag />
          </ListItemIcon>
          <ListItemText>Flag for Follow-up</ListItemText>
        </MenuItem>
      </Menu>

      {/* Meeting Dialog */}
      <Dialog open={meetingDialogOpen} onClose={() => setMeetingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Meeting</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Schedule a meeting with {threadInfo.parentName} to discuss {threadInfo.childName}'s progress.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will send a meeting request to the parent with your available time slots.
          </Alert>
          {/* Add meeting scheduling form here */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMeetingDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Send Meeting Request</Button>
        </DialogActions>
      </Dialog>

      {/* Mark as Resolved Dialog */}
      <Dialog open={markAsResolvedDialog} onClose={() => setMarkAsResolvedDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark as Resolved</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Are you sure you want to mark this conversation as resolved? This will archive the thread and notify the parent.
          </Typography>
          <FormControlLabel
            control={<Switch />}
            label="Send resolution summary to parent"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkAsResolvedDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success">Mark as Resolved</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TeacherMessageThread;
